import { differenceInCalendarDays, format } from 'date-fns';

import { ApiMarket } from '#/api/types';
import { formatDateTime } from '#/lib/date';
import { UnreachableCaseError } from '#/lib/utils';

/** Session related subset of {@link ApiMarket} used across the intermittent markets UI */
export type MarketSessionInfo = Pick<
  ApiMarket,
  | 'type'
  | 'tradingSessionStatus'
  | 'approximateNextCloseTime'
  | 'approximateNextOpenTime'
  | 'marginType'
>;

/** True for intermittent (23/5, 24/5) markets with scheduled close windows (RWAs) */
export function isIntermittentMarket(
  type: ApiMarket['type'] | undefined
): boolean {
  return type === 'perpetual235' || type === 'perpetual245';
}

/** Display label of the trading schedule, e.g. "24/5". Undefined for continuous (crypto) markets */
export function getMarketTradingHoursLabel(
  type: ApiMarket['type'] | undefined
): string | undefined {
  switch (type) {
    case 'perpetual235':
      return '23/5';
    case 'perpetual245':
      return '24/5';
    case 'perpetual':
    case undefined:
      return undefined;
    default: {
      throw new UnreachableCaseError(type);
    }
  }
}

/**
 * Display states of an intermittent market, derived from the engine
 * {@link ApiMarket.tradingSessionStatus} and the approximate transition times.
 *
 * - `open` (green, yellow while `transitionVeryClose`) — full trading
 * - `closing` (yellow) — close time reached (or engine pre-close), the engine
 *   has not confirmed the close yet ("Close Pending")
 * - `closed` (red, yellow while `transitionVeryClose`) — positions frozen
 * - `opening` (yellow) — reopen time reached (or engine pre-open), not yet
 *   confirmed open ("Open Pending")
 */
export type MarketSessionDisplayStatus =
  | 'open'
  | 'closed'
  | 'opening'
  | 'closing';

/**
 * Session display state with proximity warning flags. The flags apply to both
 * transition directions (next close while `open`, next open while `closed`)
 * and are warnings only — nothing is restricted yet while they are set:
 *
 * - `transitionInLessThan1Hour` — ≤60 min to the transition, mm:ss countdown shown
 * - `transitionVeryClose` — ≤5 min to the transition, yellow warning color
 */
export type MarketSessionDisplayState = {
  state: MarketSessionDisplayStatus;
  transitionInLessThan1Hour?: boolean;
  transitionVeryClose?: boolean;

  /**
   * Timestamp of the next scheduled transition — the close time while `open`,
   * the open time while `closed` — the countdown/time target for
   * {@link formatSessionDateOrCountdown}. Undefined while a transition is
   * pending (`opening`/`closing`) or none is scheduled.
   */
  nextTransitionMs?: number;
};

/** Starts Countdown mode */
const TRANSITION_IN_LESS_THAN_1_HOUR_THRESHOLD_MS = 60 * 60 * 1000;

/** Color switch from green/red to yellow */
const TRANSITION_VERY_CLOSE_THRESHOLD_MS = 5 * 60 * 1000;

function getTransitionProximityFlags(
  remainingMs: number
): Pick<
  MarketSessionDisplayState,
  'transitionInLessThan1Hour' | 'transitionVeryClose'
> {
  return {
    transitionInLessThan1Hour:
      remainingMs <= TRANSITION_IN_LESS_THAN_1_HOUR_THRESHOLD_MS,
    transitionVeryClose: remainingMs <= TRANSITION_VERY_CLOSE_THRESHOLD_MS,
  };
}

/** Returns null for continuous (crypto) markets which never show session status UI */
export function getMarketSessionDisplayStatus(
  info: MarketSessionInfo | undefined,
  nowMs: number
): MarketSessionDisplayState | null {
  if (!info || !isIntermittentMarket(info.type) || !info.tradingSessionStatus) {
    return null;
  }
  const sessionStatus = info.tradingSessionStatus;
  switch (sessionStatus) {
    case 'open': {
      if (!info.approximateNextCloseTime) {
        return { state: 'open' };
      }
      if (nowMs >= info.approximateNextCloseTime) {
        return {
          state: 'closing',
          transitionInLessThan1Hour: true,
          transitionVeryClose: true,
        };
      }
      return {
        state: 'open',
        nextTransitionMs: info.approximateNextCloseTime,
        ...getTransitionProximityFlags(info.approximateNextCloseTime - nowMs),
      };
    }
    case 'preClose':
      return {
        state: 'closing',
        transitionInLessThan1Hour: true,
        transitionVeryClose: true,
      };
    case 'preOpen':
      return {
        state: 'opening',
        transitionInLessThan1Hour: true,
        transitionVeryClose: true,
      };
    case 'halted':
      // Unscheduled halt, trading is restricted like over a scheduled closure
      return {
        state: 'closed',
        nextTransitionMs: info.approximateNextOpenTime,
      };
    case 'closed': {
      if (!info.approximateNextOpenTime) {
        return { state: 'closed' };
      }
      if (nowMs >= info.approximateNextOpenTime) {
        return {
          state: 'opening',
          transitionInLessThan1Hour: true,
          transitionVeryClose: true,
        };
      }
      return {
        state: 'closed',
        nextTransitionMs: info.approximateNextOpenTime,
        ...getTransitionProximityFlags(info.approximateNextOpenTime - nowMs),
      };
    }
    default: {
      throw new UnreachableCaseError(sessionStatus);
    }
  }
}

/** Effectively disables the session tick interval (no time-driven UI change to wake up for) */
const SESSION_TICK_DISABLED_DELAY_MS = 100_000_000;

/** Cap between ticks outside the countdown window — also bounds how stale the hooks' `nowMs` can get */
const SESSION_TICK_MAX_DELAY_MS = 60_000;

/**
 * Interval delay for the session display hooks: 1 second only while a
 * countdown is visible (≤1h to the next transition, incl. pending
 * open/close), otherwise the time remaining until the countdown window
 * starts, capped at 60s. Effectively disabled for continuous markets and
 * when no transition is scheduled.
 */
export function getSessionDisplayTickDelayMs(
  info: MarketSessionInfo | undefined,
  nowMs: number
): number {
  const status = getMarketSessionDisplayStatus(info, nowMs);
  if (!status) {
    return SESSION_TICK_DISABLED_DELAY_MS;
  }
  if (status.transitionInLessThan1Hour) {
    return 1000;
  }
  const { nextTransitionMs } = status;
  if (!nextTransitionMs) {
    // Nothing time-driven to update — the hooks still re-render when the
    // market API data changes
    return SESSION_TICK_DISABLED_DELAY_MS;
  }
  const msUntilCountdownWindow =
    nextTransitionMs - nowMs - TRANSITION_IN_LESS_THAN_1_HOUR_THRESHOLD_MS;
  return Math.min(
    Math.max(msUntilCountdownWindow, 1000),
    SESSION_TICK_MAX_DELAY_MS
  );
}

/**
 * Removed: WebSocket should provide update state functionality
 * Earliest session open/close transition across intermittent markets that is
 * after `afterMs` (exclusive). Undefined when there is none.
 */
// export function getNextSessionTransitionMs(
//   marketsTradingInfo: { [market: string]: MarketSessionInfo } | undefined,
//   afterMs: number
// ): number | undefined {
//   let next: number | undefined;
//   // Iterate through all markets
//   for (const marketInfo of Object.values(marketsTradingInfo ?? {})) {
//     if (!isIntermittentMarket(marketInfo.type)) {
//       continue;
//     }
//     for (const target of [
//       marketInfo.approximateNextOpenTime,
//       marketInfo.approximateNextCloseTime,
//     ]) {
//       if (target && target > afterMs && (!next || target < next)) {
//         next = target;
//       }
//     }
//   }
//   return next;
// }

/**
 * True while any intermittent market is past its API-indicated transition
 * time without the engine having confirmed the new session state yet (or is
 * in an engine pre-open/pre-close, which flips without a scheduled
 * timestamp) — the UI shows "Close Pending" / "Open Pending" and the markets
 * API should be re-polled until the state actually changes.
 */
export function hasPendingSessionTransition(
  marketsTradingInfo: { [market: string]: MarketSessionInfo } | undefined,
  nowMs: number
): boolean {
  return Object.values(marketsTradingInfo ?? {}).some(marketInfo => {
    const state = getMarketSessionDisplayStatus(marketInfo, nowMs)?.state;
    return state === 'closing' || state === 'opening';
  });
}

export function getSessionStatusColorVar(
  status: MarketSessionDisplayStatus,
  transitionVeryClose?: boolean
): string {
  switch (status) {
    case 'open':
      return transitionVeryClose ? 'var(--yellow0)' : 'var(--green4)';
    case 'closing':
    case 'opening':
      return 'var(--yellow0)';
    case 'closed':
      return transitionVeryClose ? 'var(--yellow0)' : 'var(--red3)';
    default: {
      throw new UnreachableCaseError(status);
    }
  }
}

/** 15% alpha surface behind {@link getSessionStatusColorVar} colored text */
export function getSessionStatusBackground(
  status: MarketSessionDisplayStatus,
  transitionVeryClose?: boolean
): string {
  switch (status) {
    case 'open':
      return transitionVeryClose
        ? 'rgba(var(--yellow0rgb), 0.15)'
        : 'rgba(var(--green4rgb), 0.15)';
    case 'closing':
    case 'opening':
      return 'rgba(var(--yellow0rgb), 0.15)';
    case 'closed':
      return transitionVeryClose
        ? 'rgba(var(--yellow0rgb), 0.15)'
        : 'rgba(var(--red3rgb), 0.15)';
    default: {
      throw new UnreachableCaseError(status);
    }
  }
}

/**
 * Market hours tooltip in the user's local timezone,
 * "Closes: 2026-07-17 14:00" and "Opens: 2026-07-19 15:00"
 * on separate lines, ordered by which transition comes first (open first
 * while closed). Undefined for continuous markets.
 */
export function formatMarketHoursTooltip(
  info: MarketSessionInfo | undefined
): string | undefined {
  if (!info || !isIntermittentMarket(info.type)) {
    return undefined;
  }
  const parts: string[] = [];
  if (info.approximateNextCloseTime) {
    parts.push(`Closes: ${formatDateTime(info.approximateNextCloseTime)}`);
  }
  if (info.approximateNextOpenTime) {
    const reopens = `Opens: ${formatDateTime(info.approximateNextOpenTime)}`;
    if (
      info.tradingSessionStatus === 'open' ||
      info.tradingSessionStatus === 'preClose'
    ) {
      parts.push(reopens);
    } else {
      parts.unshift(reopens);
    }
  }
  // Each part on its own line (Tooltip renders text with pre-line whitespace)
  return parts.length ? parts.join('\n') : undefined;
}

/**
 * Time of the next close/open, treated the same everywhere it is shown:
 * a live countdown "59:23" (mm:ss) within 60 minutes of it (the
 * `transitionInLessThan1Hour` window), otherwise the local time "22:00" — suffixed
 * with a day offset ("22:00+1", "22:00+2") when it falls on a later calendar
 * day.
 */
export function formatSessionDateOrCountdown(
  targetMs: number | undefined,
  nowMs: number
): string {
  if (!targetMs) {
    return '-';
  }
  const remainingMs = targetMs - nowMs;
  if (remainingMs <= TRANSITION_IN_LESS_THAN_1_HOUR_THRESHOLD_MS) {
    const totalSeconds = Math.max(0, Math.round(remainingMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }
  const dayOffset = differenceInCalendarDays(targetMs, nowMs);
  const time = format(targetMs, 'HH:mm');
  return dayOffset > 0 ? `${time}+${dayOffset}` : time;
}
